import { useEffect, useState } from 'react';
import type { DeviceState, ExtendedDeviceHealth } from '@/types';
import { collectExtendedHealth } from '@/health/device-reporter';
import {
  getProofOfPlayQueue,
  getPresenceQueue,
  getLastSyncTime,
} from '@/core/offline-store';
import { clearAllCachedMedia, getCacheSize } from '@/playback/media-cache';
import { getErrorCount } from '@/health/watchdog';
import { clearDeviceState } from '@/core/device-identity';
import { checkConnectivity } from '@/core/api-client';
import { logger, type LogRecord } from '@/core/logger';
import { platform } from '@/platform';

interface DiagnosticScreenProps {
  deviceState: DeviceState;
  onClose: () => void;
  onReset: () => void;
}

export default function DiagnosticScreen({
  deviceState,
  onClose,
  onReset,
}: DiagnosticScreenProps) {
  const [online, setOnline] = useState<boolean | null>(null);
  const [health, setHealth] = useState<ExtendedDeviceHealth | null>(null);
  const [recentLogs, setRecentLogs] = useState<LogRecord[]>(() =>
    logger.getRecent(50),
  );
  const [logLevelFilter, setLogLevelFilter] = useState<
    'all' | 'info' | 'warn' | 'error'
  >('all');
  const [busy, setBusy] = useState<string | null>(null);

  const popQueue = getProofOfPlayQueue().filter((r) => !r.synced).length;
  const presQueue = getPresenceQueue().filter((r) => !r.synced).length;
  const lastSync = getLastSyncTime();
  const cacheSize = getCacheSize();
  const errors = getErrorCount();
  const loggerStats = logger.getBufferStats();

  // Pulse the data we care about: health snapshot every 5s, log tail
  // every 2s so on-device debugging feels live.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      if (cancelled) return;
      void checkConnectivity().then((v) => !cancelled && setOnline(v));
      void collectExtendedHealth().then((v) => !cancelled && setHealth(v));
    };
    refresh();
    const healthTimer = setInterval(refresh, 5_000);
    const logTimer = setInterval(() => {
      if (!cancelled) setRecentLogs(logger.getRecent(50));
    }, 2_000);
    return () => {
      cancelled = true;
      clearInterval(healthTimer);
      clearInterval(logTimer);
    };
  }, []);

  const handleReset = async () => {
    if (
      !confirm('Reset device? This wipes all data and returns to setup screen.')
    )
      return;
    logger.warn('User triggered factory reset');
    setBusy('reset');
    try {
      await clearDeviceState();
      localStorage.clear();
      onReset();
    } finally {
      setBusy(null);
    }
  };

  const handleClearCache = async () => {
    if (!confirm('Clear all cached media? Player will re-download on next sync.'))
      return;
    setBusy('clear-cache');
    try {
      await clearAllCachedMedia();
      // Re-render so the cache-size row updates.
      setRecentLogs(logger.getRecent(50));
    } finally {
      setBusy(null);
    }
  };

  const handleForceFlush = async () => {
    setBusy('flush');
    try {
      await logger.forceFlush();
      setRecentLogs(logger.getRecent(50));
    } finally {
      setBusy(null);
    }
  };

  const rows: [string, string][] = [
    ['Board ID', deviceState.boardId || '—'],
    ['Board Code', deviceState.boardCode || '—'],
    ['Platform', platform.kind],
    ['Software Version', health?.softwareVersion ?? '…'],
    [
      'Server Status',
      online === null ? 'Checking...' : online ? 'Connected' : 'Offline',
    ],
    ['Disk Usage', health ? `${health.diskUsage}%` : '…'],
    ['Disk Free', health ? formatBytes(health.diskFreeBytes) : '…'],
    ['RAM Used', health ? formatBytes(health.ramUsedBytes) : '…'],
    ['CPU Load', health ? formatCpu(health.cpuLoad) : '…'],
    ['Uptime', health ? `${health.uptime} hours` : '…'],
    ['Firmware', health?.firmwareVersion ?? '…'],
    ['Device Model', health?.deviceModel ?? '…'],
    [
      'Resolution',
      health?.displayResolution
        ? `${health.displayResolution.width}×${health.displayResolution.height}`
        : '…',
    ],
    ['Cached Media', `${cacheSize} items`],
    ['Pending Proof-of-Play', `${popQueue} records`],
    ['Pending Presence', `${presQueue} records`],
    ['Pending Logs', `${loggerStats.pending} records`],
    [
      'Last Sync',
      lastSync ? new Date(lastSync).toLocaleTimeString() : 'Never',
    ],
    ['Error Count', `${errors}`],
  ];

  const filteredLogs = recentLogs.filter((r) =>
    logLevelFilter === 'all' ? true : r.level === logLevelFilter,
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Diagnostics</h2>
          <button onClick={onClose} style={styles.closeBtn}>
            ✕
          </button>
        </div>

        <div style={styles.table}>
          {rows.map(([label, value]) => (
            <div key={label} style={styles.row}>
              <span style={styles.label}>{label}</span>
              <span style={styles.value}>{value}</span>
            </div>
          ))}
        </div>

        <div style={styles.logsHeader}>
          <span style={styles.logsTitle}>Recent logs</span>
          <div style={styles.levelTabs}>
            {(['all', 'info', 'warn', 'error'] as const).map((lvl) => (
              <button
                key={lvl}
                onClick={() => setLogLevelFilter(lvl)}
                style={{
                  ...styles.levelTab,
                  ...(logLevelFilter === lvl ? styles.levelTabActive : null),
                }}
              >
                {lvl}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.logs}>
          {filteredLogs.length === 0 ? (
            <div style={styles.logsEmpty}>— no entries —</div>
          ) : (
            filteredLogs
              .slice()
              .reverse()
              .map((rec, i) => (
                <div key={i} style={styles.logRow}>
                  <span style={{ ...styles.logTime, color: '#555' }}>
                    {formatLogTime(rec.timestamp)}
                  </span>
                  <span
                    style={{
                      ...styles.logLevel,
                      color: LEVEL_COLOR[rec.level],
                    }}
                  >
                    {rec.level}
                  </span>
                  <span style={styles.logMsg}>{rec.message}</span>
                </div>
              ))
          )}
        </div>

        <div style={styles.actions}>
          <button
            onClick={() => window.location.reload()}
            style={styles.actionBtn}
            disabled={!!busy}
          >
            Reload App
          </button>
          <button
            onClick={handleForceFlush}
            style={styles.actionBtn}
            disabled={!!busy}
          >
            {busy === 'flush' ? '…' : 'Flush Logs'}
          </button>
          <button
            onClick={handleClearCache}
            style={styles.actionBtn}
            disabled={!!busy}
          >
            {busy === 'clear-cache' ? '…' : 'Clear Cache'}
          </button>
          <button
            onClick={handleReset}
            style={{ ...styles.actionBtn, ...styles.dangerBtn }}
            disabled={!!busy}
          >
            Reset Device
          </button>
        </div>
      </div>
    </div>
  );
}

const LEVEL_COLOR: Record<LogRecord['level'], string> = {
  debug: '#666',
  info: '#7AB8FF',
  warn: '#F0B742',
  error: '#EF4444',
};

function formatBytes(bytes: number): string {
  if (bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatCpu(load: number): string {
  if (load < 0) return '—';
  return `${Math.round(load * 100)}%`;
}

function formatLogTime(iso: string): string {
  try {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  } catch {
    return iso;
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    fontFamily: 'Inter, -apple-system, sans-serif',
  },
  card: {
    width: 720,
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: 32,
    borderRadius: 16,
    backgroundColor: '#1A1A1A',
    border: '1px solid #333',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
    margin: 0,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #333',
    backgroundColor: 'transparent',
    color: '#888',
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  table: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
    backgroundColor: '#333',
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 24,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 16px',
    backgroundColor: '#1A1A1A',
  },
  label: {
    fontSize: 13,
    color: '#888',
  },
  value: {
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    fontFamily: 'monospace',
  },
  logsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  logsTitle: {
    fontSize: 12,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  levelTabs: {
    display: 'flex',
    gap: 4,
  },
  levelTab: {
    padding: '4px 10px',
    borderRadius: 6,
    border: '1px solid #2a2a2a',
    background: 'transparent',
    color: '#888',
    fontSize: 11,
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  levelTabActive: {
    background: '#2a2a2a',
    color: '#fff',
    borderColor: '#444',
  },
  logs: {
    maxHeight: 240,
    overflowY: 'auto',
    backgroundColor: '#0E0E0E',
    border: '1px solid #2a2a2a',
    borderRadius: 10,
    padding: 8,
    marginBottom: 24,
    fontFamily: 'monospace',
    fontSize: 11,
  },
  logsEmpty: {
    color: '#444',
    padding: 12,
    textAlign: 'center',
  },
  logRow: {
    display: 'grid',
    gridTemplateColumns: '70px 50px 1fr',
    gap: 8,
    padding: '3px 4px',
    borderBottom: '1px solid #1a1a1a',
  },
  logTime: {
    fontSize: 10,
  },
  logLevel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  logMsg: {
    color: '#ddd',
    fontSize: 11,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actions: {
    display: 'flex',
    gap: 8,
  },
  actionBtn: {
    flex: 1,
    padding: '10px',
    borderRadius: 10,
    border: '1px solid #333',
    backgroundColor: '#222',
    color: '#fff',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  dangerBtn: {
    borderColor: '#7F1D1D',
    color: '#EF4444',
  },
};
